/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Form, message, Modal } from 'antd';
import { useCallback, useState } from 'react';
import BadRequestError from '~lib/badRequestError';
import { replaceAircard } from '~lib/netconnClient';
import constants from '~util/constants';
import handleAuthErr from '~util/handleAuthErr';
import logger from '~util/logger';
import AircardForm, { checkExistingAircardValues } from './components/AircardForm';

const ReplaceAircardForm = Form.create({ name: 'replace-aircard' })(
  ({ close, disabled, form, type }) => {
    const { getFieldDecorator } = form;

    const FormComponent = AircardForm.Replace[type];
    if (!!type && !FormComponent) {
      logger.error(`No form found for Aircard type "${type}"`);
      message.error(
        `No form found for Aircard type "${type}", please contact the NetConn dev team...`,
        4
      );
      close();
    }

    return (
      <Form layout="vertical" hideRequiredMark>
        <p>Please enter the new aircard information...</p>
        {!!FormComponent && (
          <FormComponent disabled={disabled} getFieldDecorator={getFieldDecorator} type={type} />
        )}
      </Form>
    );
  }
);

const ReplaceAircardModal = ({ close, config, prevAircard, updateConfig, visible }) => {
  const [loading, setLoading] = useState(false);
  const [formRef, setFormRef] = useState(null);

  const type = prevAircard?.type;
  const index = prevAircard?.index;
  const existingAircards = config?.aircards;

  // Shrug: https://medium.com/@alef.duarte/using-ant-design-form-inside-a-modal-in-react-stateless-functional-component-634f33357c80
  const saveFormRef = useCallback(node => {
    if (node !== null) setFormRef(node);
  }, []);

  const handleConfirm = () => {
    if (!!formRef?.validateFields && !!formRef?.setFields) {
      formRef.validateFields(async (errors, values) => {
        // Custom validation: check for matching values in existing aircards
        const duplicateValueErrors = checkExistingAircardValues(existingAircards, values);
        if (!!duplicateValueErrors) {
          formRef.setFields(duplicateValueErrors);
        } else if (!errors && !!values) {
          setLoading(true);
          try {
            const newAircard = buildAircard(values, prevAircard);
            const data = await replaceAircard(config, newAircard);
            setLoading(false);
            updateConfig(data);
            close();
          } catch (e) {
            handleAuthErr(err => {
              logger.error(err);
              setLoading(false);
              if (err instanceof BadRequestError) {
                message.error(`Bad Request: ${err.message || 'No message available'}`, 6);
              } else {
                message.error(constants.API_ERR_MESSAGE, 0);
                close();
              }
            })(e);
          }
        }
      });
    }
  };

  return (
    <Modal
      confirmLoading={loading}
      onOk={handleConfirm}
      cancelButtonProps={{
        type: 'link',
        disabled: false,
      }}
      closable={false}
      destroyOnClose
      maskClosable={!loading}
      okButtonProps={{
        disabled: false,
      }}
      okText="Confirm"
      onCancel={close}
      title={`Replace Aircard ${index + 1}`}
      visible={visible}
    >
      <ReplaceAircardForm ref={saveFormRef} close={close} type={type} disabled={loading} />
    </Modal>
  );
};

const buildAircard = (values, prevAircard) => ({
  id: prevAircard.id,
  type: prevAircard.type,
  ...values,
});

export default ReplaceAircardModal;
